import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { analytics } from '../lib/analytics';

export default function AuthPage({ mode = 'signin' }) {
  const { signIn, signUp, resetPassword, signInWithGoogle, signInWithGitHub } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const navigate = useNavigate();

  const [tab, setTab] = useState(mode); // signin | signup | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    setError(''); setSuccess('');
    if (!email) { setError('Email is required'); return; }

    setLoading(true);
    try {
      if (tab === 'forgot') {
        await resetPassword(email);
        setSuccess('Check your email for a reset link.');
      } else if (tab === 'signup') {
        if (!username.trim()) { setError('Username is required'); setLoading(false); return; }
        if (password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
        await signUp(email, password, username.trim().toLowerCase());
        // Send welcome email (fire and forget — don't block UX)
        fetch('/api/emails', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'welcome', email, username: username.trim().toLowerCase() }),
        }).catch(() => {});
        analytics.signUp({ method: 'email' });
        setSuccess('__CREATED__');
      } else {
        await signIn(email, password);
        analytics.login({ method: 'email' });
        navigate('/app');
      }
    } catch (e) {
      setError(e.message || 'Something went wrong');
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try { await signInWithGoogle(); }
    catch (e) { setError(e.message); setGoogleLoading(false); }
  };

  const handleGitHub = async () => {
    setGithubLoading(true);
    try { await signInWithGitHub(); }
    catch (e) { setError(e.message); setGithubLoading(false); }
  };

  const onKey = (e) => { if (e.key === 'Enter') handle(); };

  const oauthBtnStyle = (loading) => ({
    width: '100%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 8,
    background: 'var(--surface2)',
    border: '0.5px solid var(--border2)',
    borderRadius: 'var(--radius-sm)', padding: '10px',
    fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500,
    color: 'var(--text)', cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1, transition: 'border-color 0.12s',
  });

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Link to="/" style={styles.logo}>
          CopyPaste<span style={{ color: 'var(--accent)' }}>Genius</span>
        </Link>
        <p style={styles.sub}>
          {tab === 'signin' && 'Sign in to submit and manage entries'}
          {tab === 'signup' && 'Create a free account'}
          {tab === 'forgot' && 'Reset your password'}
        </p>

        {/* Tabs */}
        {tab !== 'forgot' && (
          <div style={styles.tabs}>
            <button style={styles.tab(tab === 'signin')} onClick={() => { setTab('signin'); setError(''); }}>Sign in</button>
            <button style={styles.tab(tab === 'signup')} onClick={() => { setTab('signup'); setError(''); }}>Sign up</button>
          </div>
        )}

        {/* OAuth providers */}
        {tab !== 'forgot' && success !== '__CREATED__' && (
          <>
            {/* Google — full width */}
            <button onClick={handleGoogle} disabled={googleLoading} style={oauthBtnStyle(googleLoading)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border3)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
              {!googleLoading && (
                <svg width="17" height="17" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span>{googleLoading ? 'Redirecting...' : `${tab === 'signup' ? 'Sign up' : 'Sign in'} with Google`}</span>
            </button>

            {/* Spacer between Google and GitHub */}
            <div style={{ height: 4 }} />

            {/* GitHub — full width */}
            <button onClick={handleGitHub} disabled={githubLoading}
              style={oauthBtnStyle(githubLoading)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border3)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}>
              {!githubLoading && (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--text)' }}>
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
                </svg>
              )}
              <span>{githubLoading ? 'Redirecting...' : `${tab === 'signup' ? 'Sign up' : 'Sign in'} with GitHub`}</span>
            </button>

            <div style={{ height: 4 }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: '0.5px', background: 'var(--border)' }} />
              <span style={{ fontSize: 11, color: 'var(--dim)', whiteSpace: 'nowrap' }}>or continue with email</span>
              <div style={{ flex: 1, height: '0.5px', background: 'var(--border)' }} />
            </div>
          </>
        )}

        {/* Fields */}
        <div style={styles.fields}>
          {tab === 'signup' && (
            <div style={styles.field}>
              <label style={styles.label}>Username</label>
              <input
                style={styles.input}
                placeholder="yourhandle"
                value={username}
                onChange={e => setUsername(e.target.value)}
                onKeyDown={onKey}
                autoComplete="username"
              />
            </div>
          )}
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={onKey}
              autoComplete="email"
            />
          </div>
          {tab !== 'forgot' && (
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder={tab === 'signup' ? 'At least 6 characters' : '••••••••'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={onKey}
                autoComplete={tab === 'signup' ? 'new-password' : 'current-password'}
              />
            </div>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {success === '__CREATED__' ? (
          <div style={{
            background: 'rgba(29,158,117,0.1)',
            border: '0.5px solid rgba(29,158,117,0.35)',
            borderRadius: 'var(--radius-sm)',
            padding: '14px 16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>🎉</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#5dcaa5', marginBottom: 4 }}>
              Account created!
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
              Welcome aboard. Log in now to get started.
            </div>
            <button
              onClick={async () => {
                setLoading(true);
                setError('');
                try {
                  await signIn(email, password);
                  navigate('/app');
                } catch (e) {
                  const msg = e.message || '';
                  if (msg.toLowerCase().includes('confirm') || msg.toLowerCase().includes('not confirmed')) {
                    setError('Email confirmation required — go to Supabase → Authentication → Settings and turn off "Enable email confirmations", then try again.');
                  } else if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials')) {
                    setError('Invalid credentials. The account may not have been created — please sign up again.');
                  } else {
                    setError(msg || 'Sign in failed. Please try again.');
                  }
                }
                setLoading(false);
              }}
              style={{
                width: '100%', background: '#1d9e75', color: '#fff',
                border: 'none', borderRadius: 'var(--radius-sm)',
                padding: '10px', fontFamily: 'var(--font)',
                fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}>
              {loading ? 'Logging in...' : 'Log in now →'}
            </button>
          </div>
        ) : success ? (
          <div style={styles.successMsg}>{success}</div>
        ) : null}

        <button style={styles.btn} onClick={handle} disabled={loading}>
          {loading ? 'Please wait...' : tab === 'signin' ? 'Sign in' : tab === 'signup' ? 'Create account' : 'Send reset link'}
        </button>

        {tab === 'signin' && (
          <button style={styles.ghost} onClick={() => { setTab('forgot'); setError(''); }}>
            Forgot password?
          </button>
        )}
        {tab === 'forgot' && (
          <button style={styles.ghost} onClick={() => { setTab('signin'); setError(''); }}>
            ← Back to sign in
          </button>
        )}

        <div style={styles.divider} />
        <Link to="/app" style={styles.browseLink}>Browse as guest →</Link>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: 'var(--surface)',
    border: '0.5px solid var(--border2)',
    borderRadius: 'var(--radius-lg)',
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  logo: {
    fontSize: 22,
    fontWeight: 500,
    letterSpacing: '-0.03em',
    color: 'var(--text)',
    textDecoration: 'none',
    marginBottom: 6,
  },
  sub: {
    fontSize: 13,
    color: 'var(--muted)',
    marginBottom: 22,
  },
  tabs: {
    display: 'flex',
    background: 'var(--bg)',
    borderRadius: 'var(--radius-sm)',
    padding: 3,
    marginBottom: 22,
    gap: 3,
  },
  tab: (active) => ({
    flex: 1,
    padding: '7px',
    border: 'none',
    borderRadius: 5,
    background: active ? 'var(--surface)' : 'transparent',
    color: active ? 'var(--text)' : 'var(--muted)',
    fontFamily: 'var(--font)',
    fontSize: 13,
    fontWeight: active ? 500 : 400,
    cursor: 'pointer',
    transition: 'all 0.12s',
    boxShadow: active ? '0 0 0 0.5px var(--border2)' : 'none',
  }),
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    marginBottom: 16,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: 'var(--dim)',
  },
  input: {
    background: 'var(--bg)',
    border: '0.5px solid var(--border2)',
    borderRadius: 'var(--radius-sm)',
    padding: '9px 12px',
    color: 'var(--text)',
    fontFamily: 'var(--font)',
    fontSize: 13,
    outline: 'none',
    width: '100%',
  },
  error: {
    fontSize: 12.5,
    color: 'var(--danger)',
    background: 'var(--danger-dim)',
    border: '0.5px solid rgba(226,75,74,0.25)',
    borderRadius: 'var(--radius-sm)',
    padding: '8px 12px',
    marginBottom: 12,
  },
  successMsg: {
    fontSize: 12.5,
    color: '#5dcaa5',
    background: 'rgba(29,158,117,0.1)',
    border: '0.5px solid rgba(29,158,117,0.25)',
    borderRadius: 'var(--radius-sm)',
    padding: '8px 12px',
    marginBottom: 12,
  },
  btn: {
    width: '100%',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    padding: '10px',
    fontFamily: 'var(--font)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: 10,
    transition: 'opacity 0.15s',
  },
  ghost: {
    background: 'none',
    border: 'none',
    color: 'var(--muted)',
    fontFamily: 'var(--font)',
    fontSize: 12.5,
    cursor: 'pointer',
    padding: '4px 0',
    textAlign: 'left',
    transition: 'color 0.12s',
  },
  divider: {
    height: '0.5px',
    background: 'var(--border)',
    margin: '18px 0 14px',
  },
  browseLink: {
    fontSize: 12.5,
    color: 'var(--muted)',
    textDecoration: 'none',
    textAlign: 'center',
  },
};
